import { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetCurrentUser } from '@cloudquery/api-cloud';
import { PlatformReport, usePlatformUpdateReport } from '@cloudquery/api-platform';
import { useBaseAuthenticated } from '@cloudquery/base-users';
import { captureError } from 'shared/base';

import { useReactRouterSyncWithUrlParam } from '@/hooks/useReactRouterSyncWithUrlParam';

import { ReportGridGroup, ReportGridItem, ReportYamlGroup } from '../types/report';
import { parseReportYaml, transformGroups, transformWidget } from '../utils';

export function useReportEditor(reportData?: PlatformReport) {
  const { currentMembership } = useBaseAuthenticated();
  const [editorOpen, setEditorOpen] = useReactRouterSyncWithUrlParam<boolean>({
    defaultValue: false,
    paramKey: 'editorOpen',
  });
  const { mutate: saveReport } = usePlatformUpdateReport();

  // the yaml that is on the editor
  const [editorYaml, setEditorYaml] = useState<string>('');
  // to track the currently saved report yaml
  const [savedReportYaml, setReportYaml] = useState<string>('');
  // to track the previewed/saved report versions during session
  const [reportYamlVersions, setReportYamlVersions] = useState<{
    index: number;
    versions: string[];
  }>({ index: 0, versions: [] });
  const previewReportYaml = reportYamlVersions.versions[reportYamlVersions.index];

  const [yamlParsingError, setYamlParsingError] = useState<string>();
  const [isPrivate, setIsPrivate] = useState<boolean>();
  const isReportYamlModified = savedReportYaml !== editorYaml;
  const { data: currentUser } = useGetCurrentUser();
  const currentUserIsReportOwner = reportData?.created_by?.id === currentUser?.data?.id;

  const { filters, groupedWidgets, longDescription, shortDescription, title, widgets } =
    useMemo(() => {
      try {
        const report = parseReportYaml(previewReportYaml);
        const { filters, groups, longDescription, shortDescription, title, widgets } = report;
        const items: ReportGridItem[] = widgets?.map(transformWidget) || [];
        const groupedWidgets: ReportGridGroup[] | undefined =
          groups && transformGroups(groups as ReportYamlGroup[]);
        setYamlParsingError(undefined);

        return {
          filters,
          groupedWidgets,
          longDescription,
          shortDescription,
          title,
          widgets: items,
        };
      } catch {
        if (previewReportYaml) {
          captureError(`Platform reports error: bad widgets and/or group yaml`);
          setYamlParsingError(
            'Ensure your report and its widgets configurations are in the right format',
          );
        }

        return {};
      }
    }, [previewReportYaml]);

  const pushYamlVersion = useCallback(
    (yaml: string) => {
      // only push report yaml if it does not match the latest version
      // this can happen if you preview and then save
      if (yaml !== reportYamlVersions.versions.at(-1)) {
        // when pushing a new version we need to remove any after the current index
        setReportYamlVersions((prev) => ({
          index: prev.index + 1,
          versions: [...prev.versions.slice(0, prev.index + 1), yaml],
        }));
      }
    },
    [reportYamlVersions],
  );

  const handleViewPreview = () => {
    pushYamlVersion(editorYaml);
  };

  const handleUndoChanges = () => {
    if (reportYamlVersions.versions.length === 1) {
      // then case when we have edits without any previous saves/previews
      setEditorYaml(reportYamlVersions.versions[0]);
    } else {
      setEditorYaml(reportYamlVersions.versions[reportYamlVersions.index - 1]);
      setReportYamlVersions((prev) => ({ ...prev, index: Math.max(prev.index - 1, 0) }));
    }
  };

  const handleRedoChanges = () => {
    setEditorYaml(reportYamlVersions.versions[reportYamlVersions.index + 1]);
    setReportYamlVersions((prev) => ({ ...prev, index: prev.index + 1 }));
  };

  const handleSave = useCallback(() => {
    pushYamlVersion(editorYaml);
    setReportYaml(editorYaml);
    // Note API needs title and description to be set for searching
    // we extract the latest version from the editorYaml here to not depend on state update of setting report yaml
    try {
      const { shortDescription, title } = parseReportYaml(editorYaml);
      saveReport({
        data: {
          content: editorYaml ?? '',
          description: shortDescription,
          private: isPrivate,
          title,
        },
        reportId: reportData!.id,
        teamName: currentMembership.team.name,
      });
    } catch (error) {
      captureError(`Platform report save error: ${error.message}`);
    }
  }, [pushYamlVersion, editorYaml, saveReport, isPrivate, reportData, currentMembership.team.name]);

  useEffect(() => {
    if (!reportData) return;

    const { content, private: isPrivate } = reportData;

    setEditorYaml(content);
    setReportYaml(content);
    setIsPrivate(isPrivate);
    // note that if a re-render happens we lose the yaml versions with this approach
    // we may want to store in session storage instead?
    setReportYamlVersions({
      index: 0,
      versions: [content],
    });
  }, [reportData]);

  useEffect(() => {
    const isSaved = reportData?.id;
    const isLoaded = reportData && previewReportYaml;
    if (isSaved && isLoaded && reportData?.private !== isPrivate) {
      handleSave();
    }
  }, [previewReportYaml, handleSave, isPrivate, reportData]);

  return {
    canRedoChanges: reportYamlVersions.index < reportYamlVersions.versions.length - 1,
    canUndoChanges:
      reportYamlVersions.index > 0 || editorYaml !== reportYamlVersions.versions.at(-1),
    currentUserIsReportOwner,
    editorOpen,
    editorYaml,
    filters,
    groupedWidgets,
    handleRedoChanges,
    handleSave,
    handleUndoChanges,
    handleViewPreview,
    isPublic: !isPrivate,
    isReportYamlModified,
    longDescription,
    previewReportYaml,
    savedReportYaml,
    setEditorOpen,
    setEditorYaml,
    setIsPublic: (isPublic: boolean) => setIsPrivate(!isPublic),
    shortDescription,
    title,
    widgets,
    yamlParsingError,
  };
}
